import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Alert, Badge, ProgressBar, Select, Checkbox, Input, Tabs } from '@dreamtree-org/twreact-ui';
import Can from '@rbac/Can';
import useVideoCompression from './useVideoCompression';
import {
  QUALITIES, SIZES, presetKeyOf, presetLabelOf,
  ADVANCED_OPTIONS, formatBytes,
} from './videoPresets';

// twreact's Select hands onChange the RAW VALUE, not a DOM event (and null when cleared),
// so the usual `e.target.value` reader silently never updates. Same helper as GitApps.jsx.
const selVal = (v) => (v?.target ? v.target.value : (v?.value ?? v ?? ''));

const opts = (list) => list.map((v) => (typeof v === 'string' ? { value: v, label: v } : v));

const STATUS_VARIANT = {
  idle: 'default', uploading: 'info', pending: 'warning',
  processing: 'info', completed: 'success', failed: 'error',
};

const STATUS_LABEL = {
  uploading: 'Uploading…', pending: 'Queued…', processing: 'Compressing…',
  completed: 'Done', failed: 'Failed',
};

export default function VideoCompress() {
  const vc = useVideoCompression();
  const fileInput = useRef(null);
  const [quality, size] = (vc.presetKey || 'best_medium').split('_');

  const setAdv = (patch) => vc.setAdvanced((a) => ({ ...a, ...patch }));

  // Only meaningful once both sizes are known; the server records output_size_bytes on completion.
  const saved = vc.job?.input_size_bytes && vc.job?.output_size_bytes
    ? Math.round((1 - vc.job.output_size_bytes / vc.job.input_size_bytes) * 100)
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to="/" className="text-sm text-primary-600 hover:underline">← Dashboard</Link>
          <h1 className="text-xl font-bold">Compress Video</h1>
          <p className="text-sm opacity-70">
            Shrink a video with one-click presets or manual ffmpeg settings. MP4, MOV, MKV, WebM — up to 10 GB.
          </p>
        </div>
        {vc.status !== 'idle' ? (
          <Badge variant={STATUS_VARIANT[vc.status] || 'default'}>{STATUS_LABEL[vc.status] || vc.status}</Badge>
        ) : null}
      </div>

      {/* A native file input, not a twreact component: this is a browser control with drag &
          drop semantics, not one of the UI primitives the kit owns. */}
      <Card className="p-4">
        <button
          type="button"
          className="w-full rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 p-6 text-center hover:border-primary-500 transition-colors"
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); vc.chooseFile(e.dataTransfer.files?.[0]); }}
        >
          <div className="text-sm font-medium">{vc.file ? vc.file.name : 'Drop a video here, or click to choose'}</div>
          <div className="text-xs opacity-60 mt-1">
            {vc.file ? formatBytes(vc.file.size) : 'MP4, MOV, MKV, WebM · up to 10 GB'}
          </div>
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => vc.chooseFile(e.target.files?.[0])}
        />
        {/* No <track>: this is the user's own just-picked local file, so no caption source
            exists to point at. It is a preview of their input, not published content. */}
        {vc.previewUrl ? (
          <video className="mt-4 w-full max-h-72 rounded-lg bg-black" src={vc.previewUrl} controls />
        ) : null}
      </Card>

      <Card className="p-4">
        <Tabs defaultIndex={vc.mode === 'advanced' ? 1 : 0} onChange={(i) => vc.setMode(i === 1 ? 'advanced' : 'simple')}>
          <Tabs.List>
            <Tabs.Tab>Simple</Tabs.Tab>
            <Tabs.Tab>Advanced</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panels>
            <Tabs.Panel>
              <div className="space-y-4 pt-2">
                <div>
                  <div className="text-sm font-medium mb-2">Quality</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {QUALITIES.map((q) => (
                      <button
                        key={q.key}
                        type="button"
                        onClick={() => vc.setPresetKey(presetKeyOf(q.key, size))}
                        className={`rounded-lg border p-3 text-left transition-colors ${
                          quality === q.key ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <div className="text-sm font-medium">{q.label}</div>
                        <div className="text-xs opacity-60">{q.hint}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium mb-2">File size</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {SIZES.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => vc.setPresetKey(presetKeyOf(quality, s.key))}
                        className={`rounded-lg border p-3 text-left transition-colors ${
                          size === s.key ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <div className="text-sm font-medium">{s.label}</div>
                        <div className="text-xs opacity-60">{s.hint}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-xs opacity-60">Selected: {presetLabelOf(quality, size)}</p>
              </div>
            </Tabs.Panel>

            <Tabs.Panel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <Select label="Codec" value={vc.advanced.codec} options={ADVANCED_OPTIONS.codec} onChange={(v) => setAdv({ codec: selVal(v) })} />
                <Select label="Container" value={vc.advanced.format} options={ADVANCED_OPTIONS.format} onChange={(v) => setAdv({ format: selVal(v) })} />
                <Select label="Resolution" value={vc.advanced.resolution} options={ADVANCED_OPTIONS.resolution} onChange={(v) => setAdv({ resolution: selVal(v) })} />
                <Select label="Frame rate" value={vc.advanced.fps} options={ADVANCED_OPTIONS.fps} onChange={(v) => setAdv({ fps: selVal(v) })} />
                <Input
                  label="CRF (0 = lossless, 51 = worst)"
                  type="number"
                  min="0"
                  max="51"
                  value={vc.advanced.crf}
                  onChange={(e) => setAdv({ crf: e.target.value })}
                />
                <Select label="Encoder speed" value={vc.advanced.speed} options={opts(ADVANCED_OPTIONS.speed)} onChange={(v) => setAdv({ speed: selVal(v) })} />
                <Select label="Audio bitrate" value={vc.advanced.audioBitrate} options={opts(ADVANCED_OPTIONS.audioBitrate)} onChange={(v) => setAdv({ audioBitrate: selVal(v) })} />
                <div className="flex items-end">
                  <Checkbox
                    label="Remove audio entirely"
                    checked={vc.advanced.removeAudio}
                    onChange={(e) => setAdv({ removeAudio: e?.target ? e.target.checked : Boolean(e) })}
                  />
                </div>
                {/* The server coerces these two to stay compatible; say so rather than letting
                    a user's explicit choice appear to be ignored. */}
                <p className="sm:col-span-2 text-xs opacity-60">
                  WebM always encodes as VP9, and MP4 never does — the server corrects an
                  incompatible pairing. Encoder speed does not apply to VP9.
                </p>
              </div>
            </Tabs.Panel>
          </Tabs.Panels>
        </Tabs>
      </Card>

      {vc.error ? <Alert variant="error" title="Compression failed">{vc.error}</Alert> : null}

      {vc.running ? (
        <Card className="p-4">
          <ProgressBar value={vc.progress} max={100} showValue label={STATUS_LABEL[vc.status] || 'Working…'} striped animated />
          <p className="text-xs opacity-60 mt-2">
            {vc.status === 'uploading'
              ? 'Uploading — keep this tab open.'
              : 'Encoding on the server. This continues even if progress looks paused.'}
          </p>
        </Card>
      ) : null}

      {vc.status === 'completed' && vc.job ? (
        <Card className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><div className="opacity-70">Original</div><div className="text-lg font-semibold">{formatBytes(vc.job.input_size_bytes)}</div></div>
            <div><div className="opacity-70">Compressed</div><div className="text-lg font-semibold">{formatBytes(vc.job.output_size_bytes)}</div></div>
            <div><div className="opacity-70">Saved</div><div className="text-lg font-semibold">{saved == null ? '—' : `${saved}%`}</div></div>
            <div><div className="opacity-70">Took</div><div className="text-lg font-semibold">{vc.job.duration_ms ? `${Math.round(vc.job.duration_ms / 1000)}s` : '—'}</div></div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={vc.download}>Download</Button>
            <Button variant="outline" onClick={vc.reset}>Compress another</Button>
          </div>
        </Card>
      ) : null}

      <Can module="tools" action="create" fallback={<Alert variant="warning" title="Read-only">You need the tools:create permission to compress a video.</Alert>}>
        <Button disabled={!vc.file || vc.running} onClick={vc.start}>
          {vc.running ? 'Working…' : 'Compress video'}
        </Button>
      </Can>
    </div>
  );
}
